import { useRef, useState } from 'react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import DOMPurify from 'dompurify';
import { Pencil } from 'lucide-react';
import type { BoardPersonMini } from '@hygge/shared';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { extractErrorMessage } from '@/lib/errors';
import { useBoardOverview } from '@/hooks/use-board-overview';
import { useUpdateBoard, useBoardPermission } from '@/hooks/use-boards';
import { useEmployeeNameFormatter } from '@/lib/employee-name';
import { DescriptionEditor } from '../description-editor';
import { ProjectUpdates } from './project-updates';
import { SprintBlocksSection } from './sprint-blocks-section';
import { OverviewSidebar } from './overview-sidebar';
import { SprintVerdictLine } from './sprint-verdict-line';
import { SprintCapacityBand } from './sprint-capacity-band';
import { STATUS_STYLE } from './board-status';

interface BoardOverviewProps {
  boardId: string;
  onSeeAllActivity: () => void;
  onOpenTask: (key: string) => void;
}

function personInitials(p: BoardPersonMini): string {
  return `${p.firstName[0] ?? ''}${p.lastName[0] ?? ''}`.toUpperCase();
}

function Dot() {
  return <span className="text-muted-foreground/40">·</span>;
}

export function BoardOverview({ boardId, onSeeAllActivity, onOpenTask }: BoardOverviewProps) {
  const { data, isLoading, isError } = useBoardOverview(boardId);
  // One code used to cover both; they are separate rows now, and a board may
  // well let somebody post an Update without letting them rename the board.
  const canEdit = useBoardPermission(boardId, 'board.settings');
  const canPublishUpdate = useBoardPermission(boardId, 'overview.publish');
  const updateBoard = useUpdateBoard();
  const formatName = useEmployeeNameFormatter();

  const [editingSummary, setEditingSummary] = useState(false);
  const [summaryDraft, setSummaryDraft] = useState('');
  const skipSummarySave = useRef(false);

  if (isLoading) {
    return (
      <div className="mx-auto max-w-6xl space-y-4 px-6 py-6">
        <div className="flex items-center gap-3">
          <Skeleton className="h-12 w-12 rounded-lg" />
          <Skeleton className="h-7 w-48 rounded-md" />
        </div>
        <Skeleton className="h-4 w-1/2 rounded-md" />
        <Skeleton className="h-32 w-full rounded-lg" />
        <Skeleton className="h-24 w-full rounded-lg" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="px-6 py-6 text-sm text-muted-foreground">Failed to load board overview.</div>
    );
  }

  const { board } = data;
  const initials = (board.slug?.slice(0, 2) || board.name.slice(0, 2)).toUpperCase();
  const team = board.departmentName ?? board.projectName;

  // The page reports on the sprint that is running. There is at most one — the
  // lifecycle refuses to start a second — so "the first active sprint" and "the
  // active sprint" are the same sprint, and both the verdict line and the
  // capacity band read from this one variable rather than picking separately.
  const activeSprint = data.sprints.find((s) => s.status === 'active') ?? null;
  const closedSprintCount = data.sprints.filter((s) => s.status === 'completed').length;

  const startEditSummary = () => {
    setSummaryDraft(board.summary ?? '');
    setEditingSummary(true);
  };

  const saveSummary = () => {
    if (skipSummarySave.current) {
      skipSummarySave.current = false;
      setEditingSummary(false);
      return;
    }
    setEditingSummary(false);
    const next = summaryDraft.trim();
    if (next !== (board.summary ?? '')) {
      updateBoard.mutate(
        { id: boardId, summary: next },
        { onError: (e) => toast.error(extractErrorMessage(e, 'Failed to update summary')) },
      );
    }
  };

  return (
    <div className="flex h-full overflow-hidden">
      <div className="min-w-0 flex-1 overflow-y-auto">
        <div className="mx-auto max-w-6xl space-y-6 px-6 py-6">
          {/* 1. The verdict, then the denominator behind it. These lead because
              the reader arrived from a breadcrumb that already names the board:
              identity is answered, "how is it going" is not. */}
          <SprintVerdictLine
            sprint={activeSprint}
            closedSprintCount={closedSprintCount}
            className="pt-0"
          />

          <SprintCapacityBand sprint={activeSprint} />

          {/* 2. Header */}
          <div className="space-y-2 border-t border-border pt-6">
            <div className="flex items-center gap-3">
              <div
                className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg text-base font-bold text-white"
                style={{ backgroundColor: board.color }}
              >
                {board.icon ? board.icon : initials}
              </div>
              <h1 className="min-w-0 truncate text-2xl font-semibold text-foreground">
                {board.name}
              </h1>
            </div>

            {editingSummary ? (
              <Input
                autoFocus
                value={summaryDraft}
                onChange={(e) => setSummaryDraft(e.target.value)}
                onBlur={saveSummary}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    e.currentTarget.blur();
                  }
                  if (e.key === 'Escape') {
                    e.preventDefault();
                    skipSummarySave.current = true;
                    e.currentTarget.blur();
                  }
                }}
                placeholder="Add a short summary…"
                className="h-8 max-w-xl text-sm"
              />
            ) : canEdit ? (
              <button
                type="button"
                onClick={startEditSummary}
                className="group inline-flex items-center gap-1.5 text-sm cursor-pointer"
              >
                <span className={board.summary ? 'text-foreground' : 'text-muted-foreground'}>
                  {board.summary || 'Add a short summary…'}
                </span>
                <Pencil className="h-3.5 w-3.5 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
              </button>
            ) : board.summary ? (
              <p className="text-sm text-muted-foreground">{board.summary}</p>
            ) : null}
          </div>

          {/* 3. Property row (read-only) */}
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1.5">
              <span className={cn('h-2 w-2 rounded-full', STATUS_STYLE[board.status].dot)} />
              {STATUS_STYLE[board.status].label}
            </span>

            {board.priority !== 'none' && (
              <>
                <Dot />
                <span className="capitalize">{board.priority}</span>
              </>
            )}

            {board.lead && (
              <>
                <Dot />
                <span className="inline-flex items-center gap-1.5">
                  <Avatar className="h-5 w-5">
                    {board.lead.avatarUrl && <AvatarImage src={board.lead.avatarUrl} />}
                    <AvatarFallback className="text-[9px]">
                      {personInitials(board.lead)}
                    </AvatarFallback>
                  </Avatar>
                  {formatName({ firstName: board.lead.firstName, lastName: board.lead.lastName })}
                </span>
              </>
            )}

            {board.targetDate && (
              <>
                <Dot />
                <span>{format(new Date(board.targetDate), 'MMM d, yyyy')}</span>
              </>
            )}

            {team && (
              <>
                <Dot />
                <span>{team}</span>
              </>
            )}
          </div>

          {/* 4. Description */}
          <section>
            <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Description
            </h2>
            {canEdit ? (
              <DescriptionEditor
                value={board.description ?? ''}
                saveOnBlur
                onChange={(html) =>
                  updateBoard.mutate(
                    { id: boardId, description: html },
                    { onError: (e) => toast.error(extractErrorMessage(e, 'Failed to update description')) },
                  )
                }
                placeholder="Add a description…"
              />
            ) : board.description ? (
              <div
                className="prose prose-sm dark:prose-invert max-w-none text-sm"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(board.description) }}
              />
            ) : (
              <p className="text-sm text-muted-foreground">—</p>
            )}
          </section>

          {/* 5. Project updates composer — only for roles this board allows to post */}
          {canPublishUpdate && <ProjectUpdates boardId={boardId} />}

          {/* 6. Sprint blocks */}
          <SprintBlocksSection
            boardId={boardId}
            sprints={data.sprints}
            activeSprintId={data.sprints.find((s) => s.status === 'active')?.id ?? null}
            onOpenTask={onOpenTask}
          />
        </div>
      </div>

      <aside className="w-80 shrink-0 overflow-y-auto border-l">
        <OverviewSidebar data={data} onSeeAllActivity={onSeeAllActivity} onOpenTask={onOpenTask} />
      </aside>
    </div>
  );
}
